import { SearchOutlined } from '@mui/icons-material';
import { Box, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import tid from '@/common/application/utils/tid';
import type { EquipmentTableData } from '@/equipments/application/types/EquipmentTypes';
import type { SelectedEquipment } from '../RaptorJobCreateModal';
import { EquipmentComplianceWarningAlert } from './EquipmentComplianceWarningAlert';
import { EquipmentSelectableListItem } from './EquipmentSelectableListItem';

interface EquipmentResource {
  type?: {
    id?: number | string | null;
  } | null;
}

interface EquipmentSelectionSectionProps {
  selectedEquipment: SelectedEquipment | null;
  equipmentSearchTerm: string;
  setEquipmentSearchTerm: (term: string) => void;
  apiEquipments: EquipmentTableData[] | undefined;
  loadingEquipments: boolean;
  loadingEquipmentFormOptions: boolean;
  isEquipmentCompliant?: (equipment: EquipmentResource) => boolean;
  getEquipmentMissingCerts?: (equipment: EquipmentResource) => string[];
  onToggleEquipment: (equipment: EquipmentTableData) => void;
}

export function EquipmentSelectionSection({
  selectedEquipment,
  equipmentSearchTerm,
  setEquipmentSearchTerm,
  apiEquipments,
  loadingEquipments,
  loadingEquipmentFormOptions,
  isEquipmentCompliant,
  getEquipmentMissingCerts,
  onToggleEquipment
}: EquipmentSelectionSectionProps) {
  const showComplianceAlert = selectedEquipment != null && isEquipmentCompliant != null && !isEquipmentCompliant(selectedEquipment);

  const list = apiEquipments ?? [];
  const showInitialSpinner = (loadingEquipmentFormOptions || loadingEquipments) && list.length === 0;
  const listDimmed = loadingEquipments && list.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Typography
        variant="h6"
        sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1, height: '24px', display: 'flex', alignItems: 'center' }}
      >
        Equipment Selection
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {showComplianceAlert && <EquipmentComplianceWarningAlert />}

        <TextField
          fullWidth
          placeholder="Search Equipment"
          value={equipmentSearchTerm}
          onChange={(e) => setEquipmentSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              )
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              backgroundColor: 'background.paper',
              height: '40px'
            }
          }}
          {...tid('jobs:create:equipment-search-input')}
        />

        <Box sx={{ minHeight: '152px', maxHeight: '152px', overflowY: 'scroll', position: 'relative' }}>
          {showInitialSpinner && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '152px'
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(2, 1fr)' },
              gap: '4px',
              alignContent: 'start',
              opacity: listDimmed ? 0.5 : 1,
              transition: 'opacity 0.15s ease-in-out',
              pointerEvents: loadingEquipments ? 'none' : 'auto'
            }}
          >
            {list.map((equipment) => (
              <EquipmentSelectableListItem
                key={equipment.id}
                equipment={equipment}
                isSelected={selectedEquipment?.id === equipment.id}
                isEquipmentCompliant={isEquipmentCompliant}
                getEquipmentMissingCerts={getEquipmentMissingCerts}
                onClick={() => onToggleEquipment(equipment)}
              />
            ))}
            {!loadingEquipments && !loadingEquipmentFormOptions && list.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1', p: 1 }}>
                No equipment found.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
